import { useState, useCallback } from "react";

type SortDirection = "asc" | "desc";

interface SortingOptions<T> {
  field: keyof T | string; // Autoriser les sous-propriétés en utilisant `string`
  direction: SortDirection;
}

function useSorting<T extends Record<string, any>>(data: T[]) {
  const [sortingOptions, setSortingOptions] = useState<SortingOptions<T>>({
    field: "", // On accepte également les sous-propriétés
    direction: "desc"
  });

  // Méthode pour accéder aux propriétés imbriquées
  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  // Fonction pour détecter si une valeur est une date au format DD/MM/YYYY
  const isDateFormat = (value: any): boolean => {
    if (typeof value !== 'string') return false;
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return dateRegex.test(value);
  };

  // Fonction pour convertir une date DD/MM/YYYY en objet Date
  const parseDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  const sortedData = !data || data.length === 0 ? data : [...data].sort((a, b) => {
    const direction = sortingOptions.direction === "asc" ? 1 : -1;
    const field = sortingOptions.field;

    // Accéder aux valeurs imbriquées comme `State.state`
    const valueA = typeof field === "string" ? getNestedValue(a, field) : a[field];
    const valueB = typeof field === "string" ? getNestedValue(b, field) : b[field];

    // Gestion spéciale pour les dates
    if (isDateFormat(valueA) && isDateFormat(valueB)) {
      const dateA = parseDate(valueA);
      const dateB = parseDate(valueB);
      return dateA > dateB ? direction : -direction;
    }

    // Comparaison par défaut
    return valueA > valueB ? direction : -direction;
  });

  const handleSort = useCallback(
    (field: keyof T | string) => {
      setSortingOptions((options) => {
        const isSameField = options.field === field;
        const direction = isSameField
          ? options.direction === "asc"
            ? "desc"
            : "asc"
          : "asc";

        return { field, direction };
      });
    },
    []
  );

  return [sortedData, handleSort, String(sortingOptions.field), sortingOptions.direction] as const;
}

export default useSorting;
